import { Drawer, Skeleton } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { useMounted } from 'src/hooks/use-mounted';
import { paths, permissions } from 'src/paths';
import { usePatientDetailsGates } from './use-patient-details-gates';
import {
  getSinglePatient,
  persistPatientIdAction,
  resetGetAllPatientPriorChargesAction,
  getAllPatientsListResetAction,
} from 'src/redux/actions/patientAction';
import { getInitials } from 'src/utils/get-initials';
import deactiveIcon from '../../icons/untitled-ui/duocolor/deactive.svg';
import PatientInfo from './patient-info';
import PatientInfoDrawer from './patient-info-drawer';
import { customersApi } from './types';
import { Customer, CustomerInvoice, CustomerLog } from './types/customer';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { multiPayerFlag } from 'src/config';
import { SQ_60_REARCHIVE_NAVIGATION } from 'src/constants/feature-treatments';
import { checkRearchivePatientAccess } from 'src/utils/rearchiveAccessCheck';
import { getRearchivePatientUrl } from 'src/utils/rearchiveUrl';
import { patientsRowTestId } from 'src/sections/dashboard/patients/test-ids';

const { patientDetails, screening, episodeOfCare, patients, deactivate, payers, mdsAssessments } =
  paths;
const { READ_EPISODE_OF_CARE, READ_SCREENINGS } = permissions;
const tabs = [
  { label: 'EOC', value: 'episode-care' },
  { label: 'Screening', value: 'screening' },
  { label: 'Info', value: 'details' },
  { label: 'MDS Assessments', value: 'mds-assessments' },
  { label: 'Payers', value: 'payers' },
];

const useCustomer = (): Customer | null => {
  const isMounted = useMounted();
  const [customer, setCustomer] = useState<Customer | null>(null);

  const handleCustomerGet = useCallback(async () => {
    try {
      const response = await customersApi.getCustomer();

      if (isMounted()) {
        setCustomer(response);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted]);

  useEffect(
    () => {
      handleCustomerGet();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return customer;
};

const useInvoices = (): CustomerInvoice[] => {
  const isMounted = useMounted();
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);

  const handleInvoicesGet = useCallback(async () => {
    try {
      const response = await customersApi.getInvoices();

      if (isMounted()) {
        setInvoices(response);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted]);

  useEffect(
    () => {
      handleInvoicesGet();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return invoices;
};

const useLogs = (): CustomerLog[] => {
  const isMounted = useMounted();
  const [logs, setLogs] = useState<CustomerLog[]>([]);

  const handleLogsGet = useCallback(async () => {
    try {
      const response = await customersApi.getLogs();

      if (isMounted()) {
        setLogs(response);
      }
    } catch (err) {
      console.error(err);
    }
  }, [isMounted]);

  useEffect(
    () => {
      handleLogsGet();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return logs;
};

const PatientDetails = () => {
  const location = useLocation();
  const customer = useCustomer();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const patientId = state?.patientId;
  const [popUpContent, setPopup] = useState(false);
  const anchorRef = useRef<any>(null);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const patientDataLoader = useSelector((state: any) => state.getSinglePatientReducer);
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  // Segment B: activate/deactivate + MDS-tab gates migrated to the instrumented scopeKey path.
  const { canActivatePatient, canDeactivatePatient, canAccessMdsTab } = usePatientDetailsGates();

  const [isMultiPayer] = useFeatureToggleWhenReady(multiPayerFlag, 'FACILITY');
  console.log('isMultiPayer', isMultiPayer);

  const [isRearchiveNavigationEnabled] = useFeatureToggleWhenReady(
    SQ_60_REARCHIVE_NAVIGATION,
    'ENTERPRISE'
  );

  type ArchiveTabStatus = 'hidden' | 'loading' | 'enabled' | 'disabled';
  const [archiveTabStatus, setArchiveTabStatus] = useState<ArchiveTabStatus>('hidden');

  const [openPatientInfoDetails, setOpenPatientInfoDetails] = useState(false);

  const handleClosePatientInfoDrawer = () => {
    setOpenPatientInfoDetails(false);
  };
  const handleOpenPatientInfoDrawer = () => {
    setOpenPatientInfoDetails(true);
  };
  const handleClose = useCallback((event: Event): void => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setPopup(false);
  }, []);
  const handleAddPatientPopUp = useCallback((): void => {
    setPopup((prevOpen) => !prevOpen);
  }, []);

  useEffect(() => {
    dispatch(
      getSinglePatient({
        patientId: getPatientId,
      })
    );
  }, [getPatientId]);

  // SQ-58: Archive tab – when toggle on, call Rearchive access-check to enable/disable tab
  useEffect(() => {
    if (!isRearchiveNavigationEnabled) {
      setArchiveTabStatus('hidden');
      return;
    }
    const mrn = patientData?.medicalRecordNumber?.trim();
    const facilityId =
      typeof localStorage !== 'undefined' ? localStorage.getItem('facilityId') : null;
    if (!mrn || !facilityId) {
      setArchiveTabStatus('disabled');
      return;
    }
    setArchiveTabStatus('loading');
    checkRearchivePatientAccess(mrn, facilityId)
      .then((ok) => setArchiveTabStatus(ok ? 'enabled' : 'disabled'))
      .catch(() => setArchiveTabStatus('disabled'));
  }, [isRearchiveNavigationEnabled, patientData?.medicalRecordNumber]);

  if (!customer) {
    return null;
  }

  const onDeactivatePatient = () => {
    navigate(`${patients}/${deactivate}`, {
      state: { value: !patientData?.active },
    });
  };

  const redirectToTab = (value: string) => {
    switch (value) {
      case 'details':
        navigate(`${patients}/${patientDetails}`, {
          state: { patientId: getPatientId },
        });
        break;
      case 'screening':
        navigate(`${patients}/${patientDetails}${screening}`, {
          state: { patientId: getPatientId },
        });
        break;
      case 'episode-care':
        navigate(`${patients}/${patientDetails}${episodeOfCare}`, {
          state: { patientId: getPatientId },
        });
        break;
      case 'payers':
        navigate(`${patients}/${patientDetails}${payers}`, {
          state: { patientId: getPatientId, patientData },
        });
        break;
      case 'mds-assessments':
        navigate(`${patients}/${patientDetails}${mdsAssessments}`, {
          state: { patientId: getPatientId, patientData },
        });
        break;
      case 'archive':
        if (archiveTabStatus === 'enabled') {
          const mrn = patientData?.medicalRecordNumber?.trim();
          const facilityId =
            typeof localStorage !== 'undefined' ? localStorage.getItem('facilityId') : null;
          if (mrn && facilityId) {
            const url = getRearchivePatientUrl(facilityId, mrn);
            if (url) window.location.href = url;
          }
        }
        break;
      default:
        break;
    }
  };

  const getTabsValue = () => {
    switch (location.pathname) {
      case `${patients}/${patientDetails}`:
        return 'details';
      case `${patients}/${patientDetails}${screening}`:
        return 'screening';
      case `${patients}/${patientDetails}${episodeOfCare}`:
        return 'episode-care';
      case `${patients}/${patientDetails}${payers}`:
        return 'payers';
      case `${patients}/${patientDetails}${mdsAssessments}`:
        return 'mds-assessments';
      default:
        return '';
    }
  };

  const handleBackPatient = () => {
    dispatch(persistPatientIdAction(getPatientId));
    dispatch(resetGetAllPatientPriorChargesAction());
  };

  const checkPermissionsOfTabs = (tabValue: string) => {
    switch (tabValue) {
      case 'episode-care':
        return permissionData?.includes(READ_EPISODE_OF_CARE);
      case 'screening':
        return permissionData?.includes(READ_SCREENINGS);
      case 'details':
      case 'payers':
        return isMultiPayer;
      case 'mds-assessments':
        return canAccessMdsTab;
      default:
        return false;
    }
  };

  return (
    <>
      <Seo title="Dashboard: Customer Details" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth={'xl'}>
          <Stack spacing={4}>
            <Stack spacing={4}>
              <div>
                <Link
                  color="text.primary"
                  component={RouterLink}
                  href={paths.patients}
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                  }}
                  underline="hover"
                  onClick={handleBackPatient}
                >
                  <SvgIcon sx={{ mr: 1 }}>
                    <ArrowLeftIcon />
                  </SvgIcon>
                  <Typography variant="subtitle2">Back to Patient List</Typography>
                </Link>
              </div>
              <Stack
                alignItems="flex-start"
                direction={'row'}
                justifyContent="space-between"
                spacing={4}
              >
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={2}
                >
                  {patientDataLoader?.loading ? (
                    <Stack>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
                        <Skeleton
                          animation="pulse"
                          variant="circular"
                        >
                          <Avatar sx={{ width: 70, height: 70 }} />
                        </Skeleton>
                        <Box sx={{ width: 100 }}>
                          <Typography
                            width={'100%'}
                            variant="h5"
                          >
                            <Skeleton animation="pulse" />
                          </Typography>
                          <Typography
                            width={'30%'}
                            variant="h5"
                          >
                            <Skeleton animation="pulse" />
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  ) : (
                    <Stack
                      alignItems="flex-start"
                      direction="row"
                      columnGap={1}
                      spacing={2}
                    >
                      <Avatar
                        src={`data:image/png;base64,${patientData?.profileImage}`}
                        sx={{
                          height: 64,
                          width: 64,
                        }}
                      >
                        {getInitials(patientData?.lastName) + getInitials(patientData?.firstName)}
                      </Avatar>
                      <Stack spacing={1}>
                        <Typography variant="h4">
                          {patientData?.lastName ? patientData?.lastName + ' ' : ''}
                          {patientData?.firstName ? patientData?.firstName + ' ' : ''}
                        </Typography>
                        <Stack
                          alignItems="center"
                          direction="row"
                          spacing={1}
                        >
                          <Chip
                            label={patientData?.active === true ? 'Active' : 'Inactive'}
                            size="small"
                            color={patientData?.active === true ? 'success' : 'warning'}
                            clickable={true}
                          />
                        </Stack>
                      </Stack>
                      <Divider
                        sx={{
                          height: '45px',
                          borderWidth: '1.5px',
                          borderStyle: 'solid',
                          borderColor: '#dce0e5',
                        }}
                        orientation="vertical"
                      />
                      <Stack>
                        <Tabs
                          indicatorColor="primary"
                          scrollButtons="auto"
                          textColor="primary"
                          onChange={(event, value) => redirectToTab(value)}
                          variant="scrollable"
                          value={getTabsValue()}
                        >
                          {tabs
                            ?.filter((tab) => checkPermissionsOfTabs(tab.value))
                            .map((tab: any) => {
                              return (
                                <Tab
                                  key={tab.value}
                                  label={tab.label}
                                  value={tab.value}
                                  data-testid={patientsRowTestId('tab', tab.value)}
                                />
                              );
                            })}
                          {archiveTabStatus !== 'hidden' && (
                            <Tab
                              key="archive"
                              label={archiveTabStatus === 'loading' ? 'Archive...' : 'Archive'}
                              value="archive"
                              disabled={
                                archiveTabStatus === 'loading' || archiveTabStatus === 'disabled'
                              }
                            />
                          )}
                        </Tabs>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
                <Stack
                  alignItems="center"
                  direction="row"
                  spacing={2}
                >
                  <Stack>
                    <Button
                      onClick={handleOpenPatientInfoDrawer}
                      variant="outlined"
                    >
                      Patient Details
                    </Button>
                  </Stack>
                  {((patientData?.active && canDeactivatePatient) ||
                    (!patientData?.active && canActivatePatient)) && (
                    <Stack
                      alignItems="center"
                      direction="row"
                      spacing={2}
                    >
                      <Button
                        color="error"
                        variant="outlined"
                        onClick={onDeactivatePatient}
                        disabled={patientData?.active ? !canDeactivatePatient : !canActivatePatient}
                      >
                        <img
                          style={{ marginRight: '.5rem' }}
                          src={deactiveIcon}
                          alt=""
                        />{' '}
                        {patientData?.active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Stack>
              {/* <div>
                <Tabs
                  indicatorColor="primary"
                  scrollButtons="auto"
                  sx={{ mt: 3 }}
                  textColor="primary"
                  onChange={(event, value) => redirectToTab(value)}
                  variant="scrollable"
                  value={getTabsValue()}
                >
                  {tabs
                    ?.filter((tab) => {
                      if (
                        tab?.value === 'episode-care' &&
                        permissionData?.includes(READ_EPISODE_OF_CARE)
                      )
                        return true;
                      if (tab?.value === 'screening' && permissionData?.includes(READ_SCREENINGS))
                        return true;
                      if (tab?.value === 'details') return true;
                      if (tab?.value === 'payers') return true;
                      return false;
                    })
                    .map((tab: any) => {
                      return (
                        <Tab
                          key={tab.value}
                          label={tab.label}
                          value={tab.value}
                        />
                      );
                    })}
                </Tabs>
                <Divider />
              </div> */}
            </Stack>
            <Outlet />
            {location.pathname === `${patients}/${patientDetails}` && (
              <div>
                <PatientInfo patientId={getPatientId} />
              </div>
            )}
          </Stack>
        </Container>
        <Drawer
          anchor="right"
          onClose={handleClosePatientInfoDrawer}
          open={openPatientInfoDetails}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 444,
            },
          }}
        >
          <PatientInfoDrawer
            patientId={getPatientId}
            handleClosePatientInfoDrawer={handleClosePatientInfoDrawer}
          />
        </Drawer>
      </Box>
    </>
  );
};

export default PatientDetails;
